import { Columns3 } from "lucide-react";
import { cn } from "../../lib/utils";
import { AnchoredPanel } from "./MoreFiltersPanel";

/**
 * Column chooser — which of a table's columns are shown.
 *
 * <p>A small checklist in an anchored panel, one checkbox per column. The HOST
 * owns the hidden set (and persists it, if it persists it); this component
 * reports toggles and nothing else. A column the host marks `locked` renders
 * checked-and-disabled — the identity column of a list is not something a
 * reader should be able to remove and then wonder where the rows went.</p>
 */
export interface ColumnChooserColumn {
  key: string;
  label: string;
  /** Always shown; the checkbox renders disabled. */
  locked?: boolean;
}

export interface ColumnChooserProps {
  columns: ColumnChooserColumn[];
  /** Keys currently hidden. */
  hiddenKeys: readonly string[];
  onChange: (hiddenKeys: string[]) => void;
  label?: string;
  className?: string;
  "data-testid"?: string;
}

export function ColumnChooser({
  columns,
  hiddenKeys,
  onChange,
  label = "Columns",
  className,
  "data-testid": testId,
}: ColumnChooserProps) {
  const hidden = new Set(hiddenKeys);
  const toggle = (key: string) => {
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    // Emitted in COLUMN order, so the persisted value is stable across
    // sessions regardless of the order the reader clicked in.
    onChange(columns.filter((c) => next.has(c.key)).map((c) => c.key));
  };
  const hiddenCount = columns.filter((c) => hidden.has(c.key) && !c.locked).length;

  return (
    <AnchoredPanel
      label={label}
      align="right"
      className={className}
      data-testid={testId ? `${testId}-panel` : undefined}
      trigger={({ open, ref, toggle: toggleOpen }) => (
        <button
          ref={ref}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={hiddenCount > 0 ? `${label} (${hiddenCount} hidden)` : label}
          onClick={toggleOpen}
          data-testid={testId}
          className={cn(
            "inline-flex h-10 items-center gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 text-small text-onyx",
            "transition-colors duration-fast ease-standard hover:bg-white-warm focus-visible:outline-none focus-visible:shadow-goldRing",
          )}
        >
          <Columns3 size={14} strokeWidth={1.5} aria-hidden />
          {label}
          {hiddenCount > 0 && (
            // `text-label` (12px) is the smallest type token; the count reads at that size.
            <span className="rounded-pill bg-white-warm px-space-1 text-label font-bold tabular-nums text-gray-mid">
              {hiddenCount} hidden
            </span>
          )}
        </button>
      )}
    >
      <div className="grid gap-space-2" role="group" aria-label={label}>
        {columns.map((col) => (
          <label key={col.key} className="flex items-center gap-space-2 text-small text-onyx">
            <input
              type="checkbox"
              checked={col.locked || !hidden.has(col.key)}
              disabled={col.locked}
              onChange={() => toggle(col.key)}
              data-testid={testId ? `${testId}-${col.key}` : undefined}
            />
            {col.label}
          </label>
        ))}
      </div>
    </AnchoredPanel>
  );
}
